// Courses.tsx (Updated with Base Fee Display)
import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Menu } from 'primereact/menu';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useSuperAdminDashboardStore } from '../../stateManagers/stores/superAdminDashboardStore';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import AddCourseModal from './AddCourseModal';
import CustomConfirmDialog from '../../components/CustomConfirmDialog';
import AddSubjectModal from '../Subjects/AddSubjectModal';
import AddFeesSlabModal from './AddFeesSlabModal';

interface Subject {
  subject_id: string;
  subject_name: string;
}

interface FeesSlab {
  fees_slab_id: string;
  base_fee: number;
  minimum_fee: number;
  maximum_fee: number;
  status: string;
  created_at: string | null;
}

interface Course {
  course_id: string;
  institute_id: string;
  name: string;
  title: string;
  description: string | null;
  status: string;
  subjects: Subject[];
  fees_slab: FeesSlab | null;
  created_at: string | null;
}

interface ApiResponse {
  success: boolean;
  data: Course[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

const Courses: React.FC = () => {
  const toast = useRef<Toast>(null);
  const menuRefs = useRef<{ [key: string]: Menu | null }>({});
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddSubjectModal, setShowAddSubjectModal] = useState(false);
  const [showAddFeesSlabModal, setShowAddFeesSlabModal] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [selectedCourseName, setSelectedCourseName] = useState<string>('');
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    visible: boolean;
    courseId: string;
    courseName: string;
  }>({
    visible: false,
    courseId: '',
    courseName: ''
  });

  const { data: dashboardData, loading: dashboardLoading, fetchDashboardData } = useSuperAdminDashboardStore();

  useEffect(() => {
    if (!dashboardData) {
      fetchDashboardData();
    }
  }, [dashboardData, fetchDashboardData]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .course-card .p-card-body {
        padding: 0 !important;
      }
      .course-card .p-card-content {
        padding: 0 !important;
      }
      .p-menu {
        border-radius: 12px !important;
        border: 1px solid #e0e0e0 !important;
        box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important;
        padding: 8px !important;
      }
      .p-menu .p-menuitem-link {
        border-radius: 8px !important;
        padding: 10px 12px !important;
        margin: 2px 0 !important;
        transition: all 0.2s ease !important;
      }
      .p-menu .p-menuitem-link:hover {
        background: #f5f5f5 !important;
      }
      .p-menu .p-menuitem-icon {
        color: #0f3d3e !important;
        font-size: 0.9rem !important;
      }
      .p-menu .p-menuitem-text {
        color: #1a1a1a !important;
        font-weight: 600 !important;
        font-size: 0.85rem !important;
      }
      .subject-chip {
        position: relative;
      }

      .subject-chip:hover::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        background: #0f3d3e;
        color: white;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 0.78rem;
        white-space: nowrap;
        z-index: 100;
        pointer-events: none;
        box-shadow: 0 3px 10px rgba(0,0,0,0.2);
        margin-bottom: 10px;
        opacity: 0;
        transition: opacity 0.18s ease;
      }

      .subject-chip:hover::after {
        opacity: 1;
      }

      .subject-chip:hover::before {
        content: '';
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: #0f3d3e;
        margin-bottom: 4px;
        z-index: 100;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [page, search]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(search && { search })
      });

      const response = await apiClient.get<ApiResponse>(`/v1/courses?${params}`);
      
      if (response.data.success) {
        setCourses(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to load courses');
      }
    } catch (err: any) {
      console.error('Failed to fetch courses:', err);
      setError(err.response?.data?.error || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = async (courseId: string, courseName: string) => {
    setDeleteDialog({
      visible: true,
      courseId,
      courseName
    });
  };

  const confirmDelete = async () => {
    try {
      await apiClient.delete(`/v1/courses/${deleteDialog.courseId}`);
      
      toast.current?.show({
        severity: 'success',
        summary: 'Deleted',
        detail: 'Course deleted successfully',
        life: 3000
      });
      
      fetchCourses();
    } catch (err: any) {
      console.error('Failed to delete course:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.error || 'Failed to delete course',
        life: 4000
      });
    }
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
    setShowAddModal(true);
  };

  const handleAddSubject = (courseId: string, courseName: string) => {
    setSelectedCourseId(courseId);
    setSelectedCourseName(courseName);
    setShowAddSubjectModal(true);
  };

  const handleAddFeesSlab = (courseId: string, courseName: string) => {
    setSelectedCourseId(courseId);
    setSelectedCourseName(courseName);
    setShowAddFeesSlabModal(true);
  };

  const getMenuItems = (course: Course) => [
    {
      label: 'Add Subject',
      icon: 'pi pi-plus-circle',
      command: () => handleAddSubject(course.course_id, course.name)
    },
    {
      label: 'Add Fees Slab',
      icon: 'pi pi-money-bill',
      command: () => handleAddFeesSlab(course.course_id, course.name)
    },
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: () => handleEditCourse(course)
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: () => handleDeleteCourse(course.course_id, course.name)
    }
  ];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (dashboardLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <ProgressSpinner 
          style={{ width: '50px', height: '50px' }} 
          strokeWidth="3"
        />
        <p style={{ color: '#0f3d3e', fontSize: '0.9rem' }}>Loading dashboard data...</p>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={{ padding: '24px' }}>
        <Message
          severity="warn"
          text="Unable to load dashboard data. Please refresh the page."
          style={{ fontSize: '0.9rem' }}
        />
      </div>
    );
  }

  if (loading && courses.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        background: '#f5f5f5',
        borderRadius: '12px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner 
            style={{ width: '50px', height: '50px' }} 
            strokeWidth="3"
          />
          <p style={{
            marginTop: '16px',
            color: '#0f3d3e',
            fontSize: '0.95rem'
          }}>
            Loading courses...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0', margin: '0 auto', minHeight: '100vh' }}>
      <Toast ref={toast} />
      <CustomConfirmDialog
        visible={deleteDialog.visible}
        onHide={() => setDeleteDialog({ visible: false, courseId: '', courseName: '' })}
        onConfirm={confirmDelete}
        title="Delete Course"
        message={`Are you sure you want to delete "${deleteDialog.courseName}"? This action cannot be undone and all associated data will be permanently removed.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="danger"
        icon="pi pi-trash"
      />
      
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 40px rgba(15, 61, 62, 0.3)'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: 'rgba(212, 241, 126, 0.1)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div>
              <div style={{
                display: 'inline-block',
                padding: '5px 12px',
                background: 'rgba(212, 241, 126, 0.2)',
                borderRadius: '20px',
                marginBottom: '10px',
                fontSize: '0.7rem',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>
                Course Management
              </div>
              <h1 style={{
                margin: '0 0 8px 0',
                fontSize: '1.7rem',
                fontWeight: '700',
                color: 'white',
                letterSpacing: '-0.5px'
              }}>
                Courses
              </h1>
              <p style={{
                margin: '0',
                color: 'rgba(255,255,255,0.8)',
                fontSize: '0.9rem'
              }}>
                Manage all courses across institutes
              </p>
              <div style={{
                marginTop: '12px',
                display: 'flex',
                gap: '16px',
                fontSize: '0.85rem',
                color: 'rgba(255,255,255,0.7)'
              }}>
                <span><i className="pi pi-book" style={{ marginRight: '6px' }}></i>{pagination.total} Total Courses</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <GlobalButton
                label="Add Course"
                variant="success"
                icon="pi pi-plus"
                onClick={() => {
                  setEditingCourse(null);
                  setShowAddModal(true);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0'
      }}>
        <div style={{ position: 'relative' }}>
          <i className="pi pi-search" style={{
            position: 'absolute',
            left: '14px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#999',
            fontSize: '0.9rem'
          }}></i>
          <InputText
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search courses by name or title..."
            style={{
              width: '100%',
              paddingLeft: '42px',
              height: '44px',
              border: '2px solid #f0f0f0',
              borderRadius: '12px',
              fontSize: '0.9rem'
            }}
          />
        </div>
      </div>

      {error && (
        <Message 
          severity="error" 
          text={error}
          style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }}
        />
      )}

      {/* Courses Grid */}
      {courses.length === 0 && !loading ? (
        <div style={{
          padding: '80px 20px',
          textAlign: 'center',
          background: 'white',
          borderRadius: '16px',
          border: '2px dashed #e0e0e0'
        }}>
          <i className="pi pi-book" style={{ fontSize: '3rem', color: '#ddd', marginBottom: '16px' }}></i>
          <h3 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '1.1rem', fontWeight: '600' }}>
            No courses found
          </h3>
          <p style={{ margin: '0 0 20px 0', color: '#999', fontSize: '0.9rem' }}>
            {search ? 'Try adjusting your search criteria' : 'Get started by adding your first course'}
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(288px, 1fr))',
          gap: '16px'
        }}>
          {courses.map((course) => {
            return (
              <Card
                key={course.course_id}
                className="course-card"
                style={{
                  borderRadius: '20px',
                  border: 'none',
                  background: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(15,61,62,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                }}
              >
                {/* Card Header with Gradient */}
                <div style={{
                  background: 'linear-gradient(180deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
                  padding: '20px',
                  color: 'white',
                  borderRadius: '20px 20px 0 0'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        margin: '0 0 6px 0',
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: 'white'
                      }}>
                        {course.name}
                      </h3>
                      <p style={{
                        margin: '0 0 4px 0',
                        fontSize: '0.8rem',
                        color: 'rgba(255,255,255,0.8)',
                        lineHeight: '1.4'
                      }}>
                        {course.title} 
                      </p>
                      {course.fees_slab && (
                      <p style={{
                        margin: 0,
                        fontSize: '0.8rem',
                        color: 'rgba(255,255,255,0.8)',
                        lineHeight: '1.4'
                      }}>
                         From <span style={{fontWeight: '600', marginLeft:'10px'}}>{formatCurrency(course.fees_slab.base_fee)}</span>
                      </p>
                      )}
                    </div>
                    <span style={{
                      padding: '5px 12px',
                      borderRadius: '20px',
                      fontSize: '0.7rem',
                      fontWeight: '600',
                      background: course.status === 'active' ? '#d4f17e' : '#ffcdd2',
                      color: course.status === 'active' ? '#0f3d3e' : '#b71c1c',
                      textTransform: 'capitalize'
                    }}>
                      {course.status}
                    </span>
                  </div>
                </div>

                {/* Card Body */}
                <div style={{ padding: '20px' }}>
                  {/* Description */}
                  {course.description && (
                    <div style={{
                      marginBottom: '16px',
                      paddingBottom: '16px',
                      borderBottom: '1px dashed #e0e0e0'
                    }}>
                      <p style={{
                        margin: '0 0 8px 0',
                        fontSize: '0.75rem',
                        color: '#999',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        DESCRIPTION
                      </p>
                      <p style={{
                        margin: 0,
                        fontSize: '0.85rem',
                        color: '#666',
                        lineHeight: '1.5',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {course.description}
                      </p>
                    </div>
                  )}

              

                  {/* Subjects */}
                  <div style={{
                    marginBottom: '16px',
                    paddingBottom: '16px',
                    borderBottom: '1px dashed #e0e0e0'
                  }}>
                    <p style={{
                      margin: '0 0 10px 0',
                      fontSize: '0.75rem',
                      color: '#999',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      SUBJECTS
                    </p>

                    {course.subjects && course.subjects.length > 0 ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'wrap'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          position: 'relative'
                        }}>
                          {course.subjects.slice(0, 4).map((subject, index) => (
                            <div
                              key={subject.subject_id}
                              className="subject-chip"
                              data-tooltip={subject.subject_name}
                              title={subject.subject_name}
                              style={{
                                width: '38px',
                                height: '38px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
                                border: '2.5px solid white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.78rem',
                                fontWeight: '700',
                                color: '#d4f17e',
                                cursor: 'pointer',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                                marginLeft: index > 0 ? '-14px' : '0',
                                zIndex: 4 - index,
                                transition: 'all 0.2s ease',
                                position: 'relative'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.18)';
                                e.currentTarget.style.zIndex = '20';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.zIndex = String(4 - index);
                              }}
                            >
                              {subject.subject_name.charAt(0).toUpperCase()}
                            </div>
                          ))}
                        </div>

                        <span style={{
                          fontSize: '0.85rem',
                          color: '#444',
                          fontWeight: '600'
                        }}>
                          {course.subjects.length} {course.subjects.length === 1 ? 'subject' : 'subjects'}
                        </span>

                        {course.subjects.length > 4 && (
                          <span style={{
                            fontSize: '0.78rem',
                            color: '#888',
                            fontWeight: '500'
                          }}>
                            (+{course.subjects.length - 4} more)
                          </span>
                        )}
                      </div>
                    ) : (
                      <div style={{
                        padding: '12px',
                        background: '#f5f5f5',
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <p style={{
                          margin: 0,
                          fontSize: '0.8rem',
                          color: '#999',
                          fontStyle: 'italic'
                        }}>
                          No subjects added
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Created Date */}
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{
                      margin: '0 0 8px 0',
                      fontSize: '0.75rem',
                      color: '#999',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      CREATED
                    </p>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        background: '#0f3d3e',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <i className="pi pi-calendar" style={{ color: '#d4f17e', fontSize: '0.9rem' }}></i>
                      </div>
                      <div>
                        <p style={{
                          margin: 0,
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          color: '#1a1a1a'
                        }}>
                          {formatDate(course.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{
                    display: 'flex',
                    gap: '8px'
                  }}>
                    <button
                      style={{
                        flex: 1,
                        padding: '12px',
                        background: '#0f3d3e',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#1a5555';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#0f3d3e';
                      }}
                    >
                      <i className="pi pi-eye" style={{ fontSize: '0.9rem' }}></i>
                      View Details
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        menuRefs.current[course.course_id]?.toggle(e);
                      }}
                      style={{
                        padding: '12px',
                        background: '#f5f5f5',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#0f3d3e',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e0e0e0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f5f5f5';
                      }}
                    >
                      <i className="pi pi-ellipsis-v" style={{ fontSize: '0.9rem' }}></i>
                    </button>
                    <Menu
                      model={getMenuItems(course)}
                      popup
                      ref={(el) => {
                        menuRefs.current[course.course_id] = el;
                      }}
                    />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          marginTop: '32px',
          padding: '20px',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <button
            disabled={page === 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            style={{
              padding: '10px 16px',
              background: page === 1 ? '#f5f5f5' : '#0f3d3e',
              border: 'none',
              borderRadius: '10px',
              color: page === 1 ? '#999' : 'white',
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: '600'
            }}
          >
            <i className="pi pi-chevron-left"></i>
          </button>
          <span style={{
            fontSize: '0.9rem',
            color: '#0f3d3e',
            fontWeight: '600',
            padding: '0 12px'
          }}>
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            disabled={page === pagination.pages}
            onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
            style={{
              padding: '10px 16px',
              background: page === pagination.pages ? '#f5f5f5' : '#0f3d3e',
              border: 'none',
              borderRadius: '10px',
              color: page === pagination.pages ? '#999' : 'white',
              cursor: page === pagination.pages ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: '600'
            }}
          >
            <i className="pi pi-chevron-right"></i>
          </button>
        </div>
      )}

      {/* Add/Edit Course Modal */}
      <AddCourseModal
        visible={showAddModal}
        editingCourse={editingCourse}
        instituteId={dashboardData?.institutes[0]?.institute_id || ''}
        onHide={() => {
          setShowAddModal(false);
          setEditingCourse(null);
        }}
        onSuccess={() => {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: editingCourse ? 'Course updated successfully!' : 'Course created successfully!',
            life: 3000
          });
          fetchCourses();
          setShowAddModal(false);
          setEditingCourse(null);
        }}
      />

      {/* Add Subject Modal */}
      <AddSubjectModal
        visible={showAddSubjectModal}
        courseId={selectedCourseId}
        courseName={selectedCourseName}
        onHide={() => {
          setShowAddSubjectModal(false);
          setSelectedCourseId('');
          setSelectedCourseName('');
        }}
        onSuccess={() => {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Subject created successfully!',
            life: 3000
          });
          fetchCourses();
          setShowAddSubjectModal(false);
          setSelectedCourseId('');
          setSelectedCourseName('');
        }}
      />

      {/* Add Fees Slab Modal */}
      <AddFeesSlabModal
        visible={showAddFeesSlabModal}
        courseId={selectedCourseId}
        courseName={selectedCourseName}
        instituteId={dashboardData?.institutes[0]?.institute_id || ''}
        onHide={() => {
          setShowAddFeesSlabModal(false);
          setSelectedCourseId('');
          setSelectedCourseName('');
        }}
        onSuccess={() => {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Fees slab created successfully!',
            life: 3000
          });
          fetchCourses();
          setShowAddFeesSlabModal(false);
          setSelectedCourseId('');
          setSelectedCourseName('');
        }}
      />
    </div>
  );
};

export default Courses;